import { useMemo } from "react";

import { useAlertContext, useFormikWithDirtyForm } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { SystemSettingKey, systemSettingsApi } from "@/api";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface FormValues {
  translationToolUrl: string;
}

type ValidationType = { [Property in keyof FormValues]?: Yup.AnySchema };

const SETTING_KEY = SystemSettingKey.TranslationToolUrl;
const MAX_URL_LENGTH = 2048;

export function useTranslationToolSettings() {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: setting, isLoading } = useQuery({
    queryKey: [QueryKey.SystemSetting, SETTING_KEY],
    queryFn: () => systemSettingsApi.getSystemSetting({ key: SETTING_KEY }),
  });

  const updateMutation = useMutation({
    mutationFn: (value: string | null) =>
      systemSettingsApi.updateSystemSetting({
        key: SETTING_KEY,
        updateSystemSettingRequest: { value },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.SystemSetting, SETTING_KEY] });
      showSuccess(t("settings.translationTool.saveSuccess"));
    },
  });

  const initialValues = useMemo<FormValues>(
    () => ({
      translationToolUrl: typeof setting?.value === "string" ? setting.value : "",
    }),
    [setting]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape<ValidationType>({
        translationToolUrl: Yup.string()
          .max(MAX_URL_LENGTH, ({ max }) => t("common.validation.maxLength", { max }))
          .test("is-http-url", t("settings.translationTool.invalidUrl"), (value) => {
            const trimmed = value?.trim();
            if (!trimmed) {
              return true;
            }
            if (!URL.canParse(trimmed)) {
              return false;
            }
            const { protocol } = new URL(trimmed);
            return protocol === "http:" || protocol === "https:";
          }),
      }),
    [t]
  );

  const formik = useFormikWithDirtyForm<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const trimmed = values.translationToolUrl.trim();
      await updateMutation.mutateAsync(trimmed === "" ? null : trimmed);
    },
  });

  return {
    formik,
    isLoading,
    urlMaxLength: MAX_URL_LENGTH,
  };
}
